import React, { useRef, useEffect } from "react";

import {
  AlignmentType,
  Document,
  HeadingLevel,
  Packer,
  Paragraph,
  TabStopPosition,
  TabStopType,
  TextRun,
  Table,
  TableRow,
  TableCell,
  PageOrientation,
  Alignment,
  Header,
  VerticalAlign,
  HorizontalPosition,
  ShadingType,
  PageBreak,
  PageNumber,
  Footer,
} from "docx";

import { saveAs } from "file-saver";

// Scss imports
import css from "./ReactDocx.module.scss";
import { RiLandscapeLine } from "react-icons/ri";

// redux imports
import { useSelector, useDispatch } from "react-redux";
import { getCurrentUserData } from "../../redux/actions/currentUserActions";
import { getAllPagePapers } from "../../redux/actions/papersActions";
import processorItems from "../../data/processorItems";

interface ReactDocxProps {
  disabled: boolean;
  allPapersYear: any;
  allPagePapers: any;
}

const ReactDocx: React.FC<ReactDocxProps> = ({
  disabled,
  allPapersYear,
  allPagePapers,
}) => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getCurrentUserData);
  }, []);

  // current user data
  const currentUserData = useSelector(
    (state: any) => state.currentUser.currentUserData
  );

  useEffect(() => {
    dispatch(getAllPagePapers("", "", ""));
  }, []);

  const renderAddress = () => {
    let place = "";
    let municipality = "";
    let street = "";
    let apartmentNumber = "";
    if (currentUserData.place && currentUserData.place.length > 0) {
      place = currentUserData.place;
    }
    if (
      currentUserData.municipality &&
      currentUserData.municipality.length > 0
    ) {
      municipality = currentUserData.municipality;
    }
    if (currentUserData.street && currentUserData.street.length > 0) {
      street = currentUserData.street;
    }
    if (
      currentUserData.apartmentNumber &&
      currentUserData.apartmentNumber.length > 0
    ) {
      apartmentNumber = currentUserData.apartmentNumber;
    }
    return place + ", " + municipality + ", " + street + ", " + apartmentNumber;
  };

  const renderMb = () => {
    let nationalId = "";
    let identificationNumber = "";

    if (currentUserData.nationalId && currentUserData.nationalId.length > 0) {
      nationalId = currentUserData.nationalId;
    }
    if (
      currentUserData.identificationNumber &&
      currentUserData.identificationNumber.length > 0
    ) {
      nationalId = currentUserData.identificationNumber;
    }
    return nationalId + ", " + identificationNumber;
  };

  const tableHeader = [
    new TableRow({
      cantSplit: false,
      tableHeader: true,

      children: [
        new TableCell({
          children: [
            new Paragraph({
              text: "Ред. бр.",
              alignment: AlignmentType.CENTER,
            }),
          ],
          verticalAlign: VerticalAlign.CENTER,
          shading: {
            fill: "#DCDCDC",
          },
        }),
        new TableCell({
          children: [
            new Paragraph({
              text: "Основни број",
              alignment: AlignmentType.CENTER,
            }),
          ],
          verticalAlign: VerticalAlign.CENTER,
          shading: {
            fill: "#DCDCDC",
          },
        }),
        new TableCell({
          children: [
            new Paragraph({ text: "Подброј", alignment: AlignmentType.CENTER }),
          ],
          verticalAlign: VerticalAlign.CENTER,
          shading: {
            fill: "#DCDCDC",
          },
        }),
        new TableCell({
          children: [
            new Paragraph({ text: "Датум", alignment: AlignmentType.CENTER }),
          ],
          verticalAlign: VerticalAlign.CENTER,
          shading: {
            fill: "#DCDCDC",
          },
        }),
        new TableCell({
          children: [
            new Paragraph({
              text: "Пошиљалац/Прималац",
              alignment: AlignmentType.CENTER,
            }),
          ],
          verticalAlign: VerticalAlign.CENTER,
          shading: {
            fill: "#DCDCDC",
          },
        }),
        /*new TableCell({
          children: [
            new Paragraph({ text: "МБ/ЈМБГ", alignment: AlignmentType.CENTER }),
          ],
          verticalAlign: VerticalAlign.CENTER,
          shading: {
            fill: "#DCDCDC",
          },
        }),*/
        new TableCell({
          children: [
            new Paragraph({
              text: "Број акта пошиљаоца",
              alignment: AlignmentType.CENTER,
            }),
          ],
          verticalAlign: VerticalAlign.CENTER,
          shading: {
            fill: "#DCDCDC",
          },
        }),
        new TableCell({
          children: [
            new Paragraph({
              text: "Датум акта пошиљаоца",
              alignment: AlignmentType.CENTER,
            }),
          ],
          verticalAlign: VerticalAlign.CENTER,
          shading: {
            fill: "#DCDCDC",
          },
        }),
        new TableCell({
          children: [
            new Paragraph({
              text: "Кратак садржај",
              alignment: AlignmentType.CENTER,
            }),
          ],
          verticalAlign: VerticalAlign.CENTER,
          shading: {
            fill: "#DCDCDC",
          },
        }),
        new TableCell({
          children: [
            new Paragraph({
              text: "Обрађивач",
              alignment: AlignmentType.CENTER,
            }),
          ],
          verticalAlign: VerticalAlign.CENTER,
          shading: {
            fill: "#DCDCDC",
          },
        }),
        new TableCell({
          children: [
            new Paragraph({
              text: "Архивиран",
              alignment: AlignmentType.CENTER,
            }),
          ],
          verticalAlign: VerticalAlign.CENTER,
          shading: {
            fill: "#DCDCDC",
          },
        }),
      ],
    }),
  ];

  const tableBody = allPagePapers
    ? allPagePapers.map((paper, index) => {
        return new TableRow({
          cantSplit: false,
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  text: (index + 1).toString(),
                  alignment: AlignmentType.CENTER,
                }),
              ],
              verticalAlign: VerticalAlign.CENTER,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  text: paper.baseNumber.baseNumber.toString(),
                  alignment: AlignmentType.CENTER,
                }),
              ],
              verticalAlign: VerticalAlign.CENTER,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  text: paper.subnumber,
                  alignment: AlignmentType.CENTER,
                }),
              ],
              verticalAlign: VerticalAlign.CENTER,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  text: new Date(
                    paper.createdAt.split("T")[0]
                  ).toLocaleDateString("sr-RS", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  }),
                  alignment: AlignmentType.CENTER,
                }),
              ],

              verticalAlign: VerticalAlign.CENTER,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  text: paper.recipientSender,
                  alignment: AlignmentType.CENTER,
                }),
              ],
              verticalAlign: VerticalAlign.CENTER,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  text: paper.RSId,
                  alignment: AlignmentType.CENTER,
                }),
              ],
              verticalAlign: VerticalAlign.CENTER,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  text: paper.senderPaperNumber ? paper.senderPaperNumber : "",
                  alignment: AlignmentType.CENTER,
                }),
              ],
              verticalAlign: VerticalAlign.CENTER,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  text: paper.senderPaperDate
                    ? new Date(
                        paper.senderPaperDate.split("T")[0]
                      ).toLocaleDateString("sr-RS", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })
                    : "",
                  alignment: AlignmentType.CENTER,
                }),
              ],
              verticalAlign: VerticalAlign.CENTER,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  text: paper.shortText ? paper.shortText : "",
                  alignment: AlignmentType.CENTER,
                }),
              ],
            }),
            new TableCell({
              children: [
                new Paragraph({
                  text:
                    paper.processor.firstName + " " + paper.processor.lastName,
                  alignment: AlignmentType.CENTER,
                }),
              ],
            }),
            new TableCell({
              children: [
                new Paragraph({
                  text: paper.arhived === "arhived" ? "Да" : "Не",
                  alignment: AlignmentType.CENTER,
                }),
              ],
              verticalAlign: VerticalAlign.CENTER,
            }),
          ],
        });
      })
    : "";

  //data.push(allPagePapers);

  const table = new Table({
    margins: {
      top: 50,
      bottom: 50,
      left: 50,
      right: 50,
    },

    rows: [...tableHeader, ...tableBody],
  });

  const createHeader = (headerTitle) => {
    return new Paragraph({
      alignment: AlignmentType.CENTER,
      heading: HeadingLevel.HEADING_1,
      children: [
        new TextRun({
          text: `${headerTitle}`,
          style: "Heading1",
        }),
      ],
    });
  };

  const createContactInfo = (companyName, address, mb) => {
    return new Paragraph({
      alignment: AlignmentType.LEFT,
      spacing: {
        after: 150,
      },
      children: [
        new TextRun({
          text: `${companyName ? companyName : ""}`,
          style: "wellSpaced",
          break: 1,
          bold: true,
        }),
        new TextRun({
          text: `Адреса: ${address ? address : ""}`,
          style: "wellSpaced",
          break: 1,
        }),
        /*new TextRun({
          text: `${"Матични број: " + mb}`,
          style: "wellSpaced",
          break: 1,
        }),*/
      ],
    });
  };

  async function exportToWord() {
    const doc = new Document({
      styles: {
        paragraphStyles: [
          {
            id: "Heading1",
            name: "Heading 1",

            quickFormat: true,
            run: {
              size: 40,
              bold: true,
              font: "TimesNewRoman",
              color: "black",
            },
            paragraph: {
              spacing: {
                after: 120,
              },
            },
          },
          {
            id: "wellSpaced",
            name: "Well Spaced",
            basedOn: "Normal",
            quickFormat: true,
            run: {
              size: 12,
              font: "TimesNewRoman",
              color: "black",
            },
            paragraph: {
              spacing: {
                line: 276,
                before: 20 * 72 * 0.1,
                after: 20 * 72 * 0.05,
              },
            },
          },
          {
            id: "paragraphBold",
            name: "paragraphBold",
            basedOn: "Normal",
            quickFormat: true,
            run: {
              size: 12,
              bold: true,
              font: "TimesNewRoman",
              color: "black",
            },
            paragraph: {
              spacing: {
                line: 276,
                before: 20 * 72 * 0.1,
                after: 20 * 72 * 0.05,
              },
            },
          },
        ],
      },

      sections: [
        {
          properties: {
            page: {
              pageNumbers: {
                start: 1,
              },
              size: {
                orientation: PageOrientation.LANDSCAPE,
              },
            },
          },
          /*headers: {
            default: new Header({
              children: [
                new Paragraph({
                  children: [
                    new TextRun("Foo Bar corp. "),
                    new TextRun({
                      children: ["Page Number ", PageNumber.CURRENT]
                    }),
                    new TextRun({
                      children: [" to ", PageNumber.TOTAL_PAGES]
                    })
                  ]
                })
              ]
            })
          },*/
          footers: {
            default: new Footer({
              children: [
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  children: [
                    new TextRun({
                      children: ["Број стана: ", PageNumber.CURRENT],
                    }),
                    new TextRun({
                      children: [" од ", PageNumber.TOTAL_PAGES],
                    }),
                  ],
                }),
              ],
            }),
          },
          children: [
            createHeader("Деловодник"),
            createContactInfo(
              currentUserData.lastName
                ? "Име и презиме: " +
                    currentUserData.firstName +
                    " " +
                    currentUserData.lastName
                : "Име фирме: " + currentUserData.businessName,
              renderAddress(),
              renderMb()
            ),
            table,
          ],
        },
      ],
    });

    const buffer = await Packer.toBuffer(doc);

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
    //const link = document.createElement("a");
    //saveAs(blob, `delovodnik-za-${allPapersYear}-godinu`);
    //link.href = window.URL.createObjectURL(blob);
    //link.download = "my.docx";
    //link.click();
  }
  return (
    <div onClick={exportToWord} className={css.buttonWrapper}>
      <button disabled={disabled}>Иштампај</button>
    </div>
  );
};

export default ReactDocx;
